import BoxCard from "shared/components/box/BoxCard.js";
import ItemRowClub from "shared/components/items/ItemRowClub.js";
import LoadingSquare from "shared/components/loading/LoadingSquare";
import MiscCompetitionRanking from "shared/components/misc/MiscCompetitionRanking";
import MiscArenaManagerStats from "shared/components/misc/MiscArenaManagerStats";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getClubs, getFullCompetitionsLimited } from "shared/services/api-assistant.js";
import { getArenaManager } from "shared/services/api-mfl.js";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Count from "shared/components/counts/Count.js";

const PageUserClubs = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const user = useOutletContext();
  const [clubs, setClubs] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [initialView, setInitialView] = useState(searchParams.get("view") || "ranking");
  const [seconds, setSeconds] = useState(15);
  const [arenaState, setArenaState] = useState(null);
  const hasArenaClub = clubs?.some((club) => club.type === "ARENA");
  const competitionClubCount = clubs?.filter((club) => club.type !== "ARENA").length || 0;
  const hasCompetitionClub = competitionClubCount > 0;
  const userId = user?.id;
  const userAddress = user?.address;

  useEffect(() => {
    let active = true;
    setClubs(null);
    setCompetitions([]);
    if (!userId) return;

    const fetchCompetitions = async (clubIds) => {
      for (let i = 0; i < clubIds.length && active; i += 2) {
        await new Promise((resolve) => {
          getFullCompetitionsLimited({
            handleSuccess: (v) => {
              if (active) setCompetitions((prev) => [...prev, ...v.data.getCompetitions]);
              resolve();
            },
            handleError: (e) => {
              console.log("Error fetching competition", e);
              resolve();
            },
            params: { participants: clubIds.slice(i, i + 2), status: "LIVE" },
          });
        });
      }
    };

    getClubs({
      handleSuccess: (d) => {
        if (!active) return;
        const fetchedClubs = d.data.getClubs.sort((a, b) => {
          if (a.type === "ARENA" && b.type !== "ARENA") return -1;
          if (a.type !== "ARENA" && b.type === "ARENA") return 1;
          return a.division - b.division;
        });
        setClubs(fetchedClubs);
        fetchCompetitions(fetchedClubs.filter((club) => club.type !== "ARENA").map((club) => club.id));
      },
      handleError: (e) => console.log(e),
      params: { owners: [userId], considerStaffMembers: true },
    });
    return () => {
      active = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!hasArenaClub || !userAddress) return;
    const controller = new AbortController();
    setArenaState({ address: userAddress, loading: true });
    getArenaManager({ address: userAddress, signal: controller.signal })
      .then((manager) => {
        if (!controller.signal.aborted) {
          setArenaState({ address: userAddress, manager });
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setArenaState({ address: userAddress, error: true });
          console.log("Error fetching Arena manager", error);
        }
      });
    return () => controller.abort();
  }, [hasArenaClub, userAddress]);

  const getArenaBlock = () => {
    if (!userAddress) return <div className="text-secondary py-3">No wallet address available for Arena stats.</div>;
    if (!arenaState || arenaState.address !== userAddress || arenaState.loading) {
      return <LoadingSquare height={300} />;
    }
    if (arenaState.error || !arenaState.manager) {
      return <div className="text-secondary py-3" role="status">Arena stats are currently unavailable.</div>;
    }
    return <MiscArenaManagerStats manager={arenaState.manager} />;
  };

  const getCompetitionBlocks = (clubId) => {
    if (competitions.length === 0) return [];

    const maxSeason = Math.max(...competitions.map((c) => c.season));
    const filtered = competitions.filter((c) => c.season === maxSeason && c.participants?.some((p) => p.id === clubId));

    const sorted = filtered.sort((a, b) => (a.type === "LEAGUE" ? -1 : b.type === "LEAGUE" ? 1 : 0));

    if (sorted.length === 0) return null;

    return sorted.map((competition) => (
      <MiscCompetitionRanking
        key={competition.id}
        competition={competition}
        clubToHighlight={[clubId]}
        initialView={initialView}
        hideProbability={competition.type === "CUP"}
      />
    ));
  };

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      const now = new Date();
      const targetTime = new Date(now);
      targetTime.setUTCHours(22, 30, 0, 0);

      if (targetTime <= now) {
        targetTime.setUTCDate(targetTime.getUTCDate() + 1);
      }

      const timeRemaining = targetTime.getTime() - now.getTime();
      const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
      const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const secondsRemaining = Math.floor((timeRemaining % (1000 * 60)) / 1000);

      setSeconds(hoursRemaining * 3600 + minutesRemaining * 60 + secondsRemaining);
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
    };
  }, []);

  return (
    <div id="PageUserClubs">
      <div className="container-xxl px-1 px-md-4 py-4">
        {user && (
          <div className="row">
            <div className="col-12">
              <div className="d-flex flex-fill flex-column flex-md-row flex-column-reverse">
                <BoxCard
                  title={
                    <div style={{ minWidth: "100px" }}>
                      {clubs !== null ? (
                        <Count label={"Club" + (competitionClubCount > 1 ? "s" : "")} count={competitionClubCount} />
                      ) : (
                        <LoadingSquare height={"50px"} />
                      )}
                    </div>
                  }
                ></BoxCard>
                {hasCompetitionClub && (
                <div className="d-flex flex-column flex-fill flex-sm-row justify-content-end align-items-center">
                  <div className="text-small me-sm-2" style={{ opacity: 0.2 }}>
                    {"Table update in "}
                    {Math.floor(seconds / 3600)
                      .toString()
                      .padStart(2, "0")}
                    :{(Math.floor(seconds / 60) % 60).toString().padStart(2, "0")}:
                    {(seconds % 60).toString().padStart(2, "0")}
                  </div>
                  <div className="d-flex flex-row ms-md-2 border me-2">
                    <button
                      className={"btn btn-small text-main"}
                      onClick={() => {
                        navigate({ search: "?view=ranking" });
                        setInitialView("ranking");
                      }}
                    >
                      Ranking
                    </button>
                    <button
                      className={"btn btn-small text-main"}
                      onClick={() => {
                        navigate({ search: "?view=projection" });
                        setInitialView("projection");
                      }}
                    >
                      Projection
                    </button>
                    <button
                      className={"btn btn-small text-main"}
                      onClick={() => {
                        navigate({ search: "?view=probability" });
                        setInitialView("probability");
                      }}
                    >
                      Probability
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>

            {(clubs || []).map((c) => (
              <div className={c.type === "ARENA" ? "col-12" : "col-12 col-xl-6"} key={c.id}>
                <BoxCard
                  content={
                    <div className="d-flex flex-column w-100">
                      <ItemRowClub c={c} />
                      <div className="mt-2">
                        {c.type === "ARENA" ? (
                          getArenaBlock()
                        ) : competitions.length > 0 && getCompetitionBlocks(c.id) !== null ? (
                          getCompetitionBlocks(c.id)
                        ) : (
                          <div style={{ height: 300 }}>
                            <LoadingSquare height={300} />
                          </div>
                        )}
                      </div>
                    </div>
                  }
                />
              </div>
            ))}
          </div>
        )}
        {(!user || clubs === null) && (
          <div style={{ height: 60 }}>
            <BoxCard content={<LoadingSquare height={60} />} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PageUserClubs;
